import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Button, Modal } from 'lego-on-react';
import { memoize, noop } from 'lodash';
import * as React from 'react';
import { Crop, PixelCrop } from 'react-image-crop';

import { Bem } from '../../service/Bem/Bem';
import { keyDownManager } from '../../service/KeyDownManager/KeyDownManager';
import { TestBem } from '../../service/TestBem';
import { KeyCode, PageStatus } from '../../typings/common';
import {
    SMART_CENTER_RATIOS,
    SMART_CENTER_RATIOS_ORDER,
    SmartCenterInfo
} from '../../typings/imageSmartCenterEditor';
import { Alert, AlertSize, AlertType } from '../Alert/Alert';
import { ImageCrop } from '../ImageCrop/ImageCrop';

import './ImageSmartCenterEditor.css';
import * as keyset from './ImageSmartCenterEditor.i18n';

const BLOCK = 'image-smart-center-editor';

const i18n = i18nFactory(keyset);

export interface StateProps {
    selectedId: string;
    imageUrl: string;
    centers: Partial<Record<SMART_CENTER_RATIOS, SmartCenterInfo>>;
    sortOrder: typeof SMART_CENTER_RATIOS_ORDER;
    imageSize: {
        height: number;
        width: number;
    };
    status: PageStatus;
    cropImageHeight?: number;
    error?: string;
}

export interface DispatchProps {
    onSelectFormat: (formatId: SMART_CENTER_RATIOS) => void;
    onChangePosition: (selectedId: string, x: number, y: number) => void;
    onSave: () => void;
    onCancel: () => void;
    onImageLoad: (imageHeight: number, imageUrl: string) => void;
}

type ImageSmartCenterEditorProps = StateProps & DispatchProps;

export class ImageSmartCenterEditor extends React.Component<
    ImageSmartCenterEditorProps
> {
    private static calcCrop = memoize(
        (props: ImageSmartCenterEditorProps): Crop => {
            const {
                selectedId,
                imageSize: { height, width }
            } = props;
            const { size, pos }: SmartCenterInfo = props.centers[selectedId];

            return {
                aspect: size.w / size.h,
                x: (100 * pos.x) / width,
                y: (100 * pos.y) / height,
                width: (100 * size.w) / width,
                height: (100 * size.h) / height
            };
        }
    );

    private keyDownManagerId: string;

    componentDidMount() {
        this.keyDownManagerId = keyDownManager.on([
            [KeyCode.ESC, this.props.onCancel]
        ]);
    }

    componentWillUnmount() {
        if (this.keyDownManagerId) {
            keyDownManager.off(this.keyDownManagerId);
        }
    }

    shouldComponentUpdate(
        nextProps: Readonly<ImageSmartCenterEditorProps>
    ): boolean {
        const {
            selectedId,
            imageUrl,
            error,
            cropImageHeight,
            status
        } = this.props;

        return (
            selectedId !== nextProps.selectedId ||
            imageUrl !== nextProps.imageUrl ||
            error !== nextProps.error ||
            cropImageHeight !== nextProps.cropImageHeight ||
            status !== nextProps.status
        );
    }

    renderSidebar() {
        const { centers, sortOrder, selectedId, onSelectFormat } = this.props;

        return sortOrder.map(id => {
            const center = centers[id];
            if (!center) {
                return null;
            }

            const { size } = center;
            const style =
                size.w > size.h
                    ? { height: `${(size.h / size.w) * 60}px` }
                    : { width: `${(size.w / size.h) * 60}px` };

            return (
                <div
                    key={id}
                    onClick={() => onSelectFormat(id)}
                    className={classNames(
                        Bem.cls(BLOCK, 'sidebar-item', {
                            selected: id === selectedId
                        }),
                        TestBem.cls(BLOCK, 'sidebar-item', {
                            selected: id === selectedId
                        })
                    )}
                >
                    <div className={Bem.cls(BLOCK, 'sidebar-item-img')}>
                        <div
                            className={Bem.cls(BLOCK, 'sidebar-item-img-box')}
                            style={style}
                        />
                    </div>
                    <div className={Bem.cls(BLOCK, 'sidebar-item-label')}>
                        {id}
                    </div>
                </div>
            );
        });
    }

    renderImageCrop() {
        return (
            <ImageCrop
                cls={Bem.cls(BLOCK, 'image-crop')}
                src={`/images${this.props.imageUrl}`}
                crop={ImageSmartCenterEditor.calcCrop(this.props)}
                theme="dark"
                locked={true}
                onComplete={this.onChangePos}
                onImageLoaded={this.onImageLoaded}
                imageStyle={{ height: this.props.cropImageHeight }}
            />
        );
    }

    onChangePos = (crop: Crop, pixelCrop: PixelCrop) =>
        this.props.onChangePosition(
            this.props.selectedId,
            pixelCrop.x,
            pixelCrop.y
        );

    onImageLoaded = (image: HTMLImageElement) =>
        this.props.onImageLoad(image.height, this.props.imageUrl);

    render() {
        const {
            error,
            onSave,
            onCancel,
            imageSize: { width, height },
            status
        } = this.props;

        return (
            <Modal
                theme="normal"
                directions={['bottom-left', 'right-bottom', 'right-center']}
                visible={true}
                autoclosable={true}
                onOutsideClick={noop}
            >
                <div className={Bem.cls(BLOCK)}>
                    <div className={Bem.cls(BLOCK, 'header')}>
                        <div className={Bem.cls(BLOCK, 'title')}>
                            {i18n('title')}
                        </div>
                        <div className={Bem.cls(BLOCK, 'subtitle')}>
                            {i18n('subtitle')}
                        </div>
                    </div>
                    <div className={Bem.cls(BLOCK, 'body')}>
                        <div className={Bem.cls(BLOCK, 'sidebar')}>
                            <div className={Bem.cls(BLOCK, 'sidebar-title')}>
                                {i18n('sidebarTitle')}
                            </div>

                            {this.renderSidebar()}
                        </div>
                        <div
                            className={Bem.cls(BLOCK, 'img-area', {
                                horizontal: width > height
                            })}
                        >
                            {this.renderImageCrop()}
                        </div>
                        {error && (
                            <Alert
                                className={Bem.cls(BLOCK, 'error')}
                                type={AlertType.error}
                                size={AlertSize.s}
                            >
                                {error}
                            </Alert>
                        )}
                    </div>
                    <div className={Bem.cls(BLOCK, 'footer')}>
                        <Button
                            cls={classNames(
                                Bem.cls(BLOCK, 'button'),
                                TestBem.cls(BLOCK, 'save-button')
                            )}
                            theme="action"
                            view="classic"
                            size="n"
                            onClick={onSave}
                            progress={status === PageStatus.SAVING}
                        >
                            {i18n('save')}
                        </Button>
                        <Button
                            cls={classNames(
                                Bem.cls(BLOCK, 'button'),
                                TestBem.cls(BLOCK, 'cancel-button')
                            )}
                            theme="normal"
                            view="classic"
                            size="n"
                            onClick={onCancel}
                        >
                            {i18n('cancel')}
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }
}
